import { OpenInNewTabIcon } from "assets";
import { UserManager, UserRoleManager } from "core";
import { useTranslation } from "i18n";
import InstagramIntegration from "pages/Profile/components/SocialMediaSection/InstagramIntegration";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import { useGetProfileSocialMediaWebsite } from "pages/Profile/hooks/useGetProfileSocialMediaWebsite";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import {
  checkIsYp,
  getUserAttribute,
  UserAttributes,
  checkIsFilmmakersProfile,
  doesUserHaveEntitlements,
  checkIsPerformer,
} from "shared-auth";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import {
  AddOnEntitlements,
  SocialMediaConnectionStatus,
  SocialMediaPlatformCode,
} from "shared-types";
import WebsiteButtonContent from "./WebsiteButtonContent";
import FilmmakersButtonContent from "./FilmmakersButtonContent";
import { useGetSocialMedia } from "./hooks/useGetSocialMedia";
import {
  handleFilmmakers,
  handleRedirectToFilmmakers,
  handleWebsite,
} from "./helper";

interface ISocialMediaSectionProps {
  isEdit: boolean;
}

const SocialMediaSection = ({ isEdit }: ISocialMediaSectionProps) => {
  const { t } = useTranslation();
  const { isPublicView } = usePublicView();

  const isInstagramIntegrationEnabled = useLaunchDarklyFlags(
    FEATURE_FLAGS.InstagramIntegration
  );

  const profileIdAttr =
    UserManager.userId || getUserAttribute(UserAttributes.MasterPortalGuid);

  const { data = {} } = useGetSocialMedia(isInstagramIntegrationEnabled);
  const { socialMedia } = useGetProfileSocialMediaWebsite();
  const { data: publicProfile } = useGetPublicProfile();

  const instagramData = data[SocialMediaPlatformCode.Instagram] || {};
  const userType = UserRoleManager.getUserType();
  const isYoungPerformer = checkIsYp(userType);

  const isFilmmakersProfile = checkIsFilmmakersProfile(userType);

  const isInstagramIntegrationVisible =
    (isInstagramIntegrationEnabled &&
      !isYoungPerformer &&
      instagramData.status) ||
    (isEdit && !isYoungPerformer);

  const isInstagramInPublicView =
    isPublicView &&
    (!instagramData.status ||
      instagramData.status === SocialMediaConnectionStatus.RequiresReauth);

  const userHasFilmmakersEntitlement = checkIsPerformer()
    ? doesUserHaveEntitlements(AddOnEntitlements.FilmmakersSync)
    : Boolean(publicProfile?.isInFilmmakers);

  const shouldShowFilmmakersSsoLink =
    userHasFilmmakersEntitlement && !isPublicView;
  const isInstagramConnected =
    instagramData.status === SocialMediaConnectionStatus.Connected;

  const isNoDataToDisplay =
    !socialMedia?.website &&
    !isInstagramIntegrationVisible &&
    !userHasFilmmakersEntitlement;

  const isSocialMediaWebsiteButtonVisible = socialMedia?.website;
  const isInstagramIntegrationButtonVisible =
    isInstagramIntegrationVisible && !isInstagramInPublicView;

  if (isNoDataToDisplay) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="hidden">{t("performer:profile.section.links")}</h2>
      {(isSocialMediaWebsiteButtonVisible ||
        isInstagramIntegrationButtonVisible) && (
        <div className="flex justify-between gap-4">
          {isSocialMediaWebsiteButtonVisible && (
            <div className="flex-1">
              <ProfileCard className="group flex flex-1 cursor-pointer items-center justify-center p-2 hover:bg-white/20">
                <button
                  onClick={() => handleWebsite(socialMedia.website)}
                  className="flex flex-1 items-center justify-center gap-2 text-m dark:text-white/90"
                >
                  {isFilmmakersProfile ? (
                    <FilmmakersButtonContent />
                  ) : (
                    <WebsiteButtonContent />
                  )}
                </button>
              </ProfileCard>
            </div>
          )}

          {isInstagramIntegrationButtonVisible && (
            <div className="flex-1">
              <InstagramIntegration
                isReadonly={isPublicView}
                status={instagramData.status}
                instagramProfileUrl={instagramData.profileUrl}
                instagramAccountId={instagramData.id}
                profileId={profileIdAttr}
                isInstgramConnected={isInstagramConnected}
                isProfileDesign
                isEdit={isEdit && !isPublicView}
              />
            </div>
          )}
        </div>
      )}

      {userHasFilmmakersEntitlement && (
        <div className="flex gap-[10px]">
          <div className="col-span-2 flex-1">
            <ProfileCard className="group flex cursor-pointer items-center justify-center p-2 hover:bg-white/20">
              <button
                onClick={() => handleFilmmakers(profileIdAttr)}
                className="flex flex-1 items-center justify-center gap-2 text-m dark:text-white/90"
              >
                <FilmmakersButtonContent />
              </button>
            </ProfileCard>
          </div>
          {shouldShowFilmmakersSsoLink && isEdit && (
            <button
              className="flex w-[30%] items-center justify-center gap-[6px] bg-transparent"
              onClick={handleRedirectToFilmmakers}
            >
              <OpenInNewTabIcon width={20} height={20} />
              <span>{t("common:profile.button.logInLink")}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SocialMediaSection;
